import React, { useState } from "react";
import { X, Check, AlertCircle, Info, HelpCircle } from "./icons";
import { NegativeFeedbackReason, FeedbackType } from "@/types/feedback";
import { cn } from "../lib/utils";

interface ChatFeedbackModalProps {
  isOpen: boolean;
  feedbackType: FeedbackType;
  onClose: () => void;
  onSubmit: (feedback: string, selectedReasons?: NegativeFeedbackReason[]) => void;
}

const feedbackReasons = [
  {
    id: 'inaccurate' as NegativeFeedbackReason,
    label: 'Inaccurate Info',
    icon: AlertCircle,
    description: 'The information provided was incorrect'
  },
  {
    id: 'lacking-details' as NegativeFeedbackReason,
    label: 'Lacking Details',
    icon: Info,
    description: 'The response needed more detail or explanation'
  },
  {
    id: 'not-helpful' as NegativeFeedbackReason,
    label: 'Not Helpful',
    icon: HelpCircle,
    description: 'The response didn\'t address the question or need'
  }
];

export function ChatFeedbackModal({ isOpen, feedbackType, onClose, onSubmit }: ChatFeedbackModalProps) {
  const [feedback, setFeedback] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<NegativeFeedbackReason[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isPositive = feedbackType === 'positive';
  const isNegative = feedbackType === 'negative';

  const handleReasonToggle = (reasonId: NegativeFeedbackReason) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId)
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmit = () => {
    if (isPositive) {
      onSubmit(feedback);
    } else {
      onSubmit(feedback, selectedReasons);
    }
    setIsSubmitted(true);
  };

  const handleClose = () => {
    setFeedback("");
    setSelectedReasons([]);
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  const title = isSubmitted 
    ? "Thank you!" 
    : isPositive 
      ? "Tell us what was helpful" 
      : "Help us improve";

  const textareaPlaceholder = isPositive
    ? "What did you find helpful about this response? (optional)"
    : "Tell us more about what could be improved...";

  const submitButtonColor = isPositive
    ? "bg-emerald-600 hover:bg-emerald-700"
    : "bg-red-600 hover:bg-red-700";

  const textareaFocusColor = isPositive
    ? "focus:ring-emerald-500"
    : "focus:ring-red-500";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-lg w-full mx-4 border border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {title}
          </h3>
          <button
            onClick={handleClose}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {!isSubmitted ? (
          <>
            {/* Negative feedback reasons selection */}
            {isNegative && (
              <div className="mb-6">
                <p className="text-zinc-300 mb-4">What went wrong with this response? (multiple select)</p>
                <div className="grid grid-cols-1 gap-3">
                  {feedbackReasons.map((reason) => {
                    const IconComponent = reason.icon;
                    const isSelected = selectedReasons.includes(reason.id);
                    return (
                      <button
                        key={reason.id}
                        onClick={() => handleReasonToggle(reason.id)}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                          isSelected
                            ? "border-red-500 bg-red-500/10 text-white"
                            : "border-zinc-600 bg-zinc-800 text-zinc-300 hover:border-zinc-500 hover:bg-zinc-750"
                        )}
                      >
                        <IconComponent 
                          size={24} 
                          className={cn(
                            "flex-shrink-0",
                            isSelected ? "text-red-400" : "text-zinc-400"
                          )} 
                        />
                        <div>
                          <div className="font-medium">{reason.label}</div>
                          <div className="text-sm opacity-75">{reason.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Text feedback input */}
            <div className="mb-4">
              {isNegative && (
                <label className="block text-zinc-300 mb-2">
                  Additional feedback (optional)
                </label>
              )}
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={textareaPlaceholder}
                className={cn(
                  "w-full h-24 px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-md text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:border-transparent resize-none",
                  textareaFocusColor
                )}
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className={cn(
                  "px-4 py-2 text-white rounded-md transition-colors",
                  submitButtonColor
                )}
              >
                Submit
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Thank you state */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                <Check size={20} className="text-white" />
              </div>
              <p className="text-zinc-300">Thank you for your feedback!</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
